import { Trophy, ExternalLink, Target, Award, Handshake } from 'lucide-react';
import type { UserData } from '@/lib/firebase/user';
import { sportDisplayLabel } from '@/lib/data/denominations';
import DOMPurify from 'dompurify';

interface SportingGoal {
    id: string;
    text: string;
}

interface PublicAboutContentProps {
    userData: UserData | null;
    missionDescription?: string;
    sportingGoals?: SportingGoal[];
}

const LEVEL_LABELS: Record<string, string> = {
    grassroots: 'Grassroots / Beginner',
    amateur: 'Amateur',
    club: 'Club / Regional',
    national: 'National',
    semi_pro: 'Semi-Professional',
    pro: 'Professional',
    elite: 'Elite / International',
};

export default function PublicAboutContent({ userData, sportingGoals }: PublicAboutContentProps) {
    if (!userData) return null;

    const sportLabel = sportDisplayLabel(userData.sport);

    const clubName = userData.club;
    const clubLink = userData.clubLink;
    const clubLocation = userData.clubLocation;
    // Same raw-key-leak shape as sportLabel above, fixed the same way: a
    // humanized fallback instead of the literal internal key when `level`
    // doesn't match one of the known values.
    const levelLabel = userData.level
        ? (LEVEL_LABELS[userData.level] || userData.level.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
        : null;
    const nextGoal = userData.nextGoal;
    const position = userData.position;
    const weightClass = userData.weightClass;
    const ageCategory = userData.ageCategory;
    const achievements = userData.achievements;
    const sponsors = userData.sponsors;

    const hasSportContextChips = !!(levelLabel || position || weightClass || ageCategory);

    return (
        <div className="max-w-3xl mx-auto py-6 px-4">
            {userData.biography && (
                <div className="prose max-w-none mb-8">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(userData.biography) }} />
                </div>
            )}

            {(userData.introVideoUpload || userData.introVideo) && (
                <div className="aspect-video">
                    {userData.introVideoUpload ? (
                        <video className="w-full h-full rounded-lg" src={userData.introVideoUpload} controls />
                    ) : (
                        <iframe
                            className="w-full h-full rounded-lg"
                            src={userData.introVideo!.replace('watch?v=', 'embed/')}
                            title="Introduction Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    )}
                </div>
            )}

            {nextGoal && (
                <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50/60 p-5">
                    <div className="flex items-start gap-3">
                        <Target className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                            <p className="text-xs uppercase tracking-wide text-amber-700/80 font-semibold mb-1">Next Goal</p>
                            <p className="text-base font-medium text-gray-900">{nextGoal}</p>
                        </div>
                    </div>
                </div>
            )}

            {sportingGoals && sportingGoals.length > 0 && (
                <div className="mt-6 rounded-lg border border-gray-200 p-4">
                    <h3 className="text-lg font-semibold text-gray-900">Season Goals</h3>
                    <div className="mt-3 space-y-3">
                        {sportingGoals.map((goal) => (
                            <div key={goal.id} className="flex items-start gap-3">
                                <span className="mt-1 text-base leading-none select-none opacity-70" aria-hidden="true" style={{ filter: 'grayscale(1)' }}>🎯</span>
                                <div className="flex-1">
                                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{goal.text}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {(clubName || clubLink || clubLocation || sportLabel || hasSportContextChips) && (
                <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs uppercase tracking-wide text-blue-700/80 font-semibold mb-2">Sport & Club</p>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                {clubName || 'Club / Team / Training base'}
                            </h3>
                            {sportLabel && (
                                <p className="text-sm text-gray-700">{sportLabel}</p>
                            )}
                            {clubLocation && (
                                <p className="text-sm text-gray-600 mt-1">{clubLocation}</p>
                            )}
                            {hasSportContextChips && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {levelLabel && (
                                        <span className="inline-flex items-center rounded-full bg-white/70 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-900">
                                            {levelLabel}
                                        </span>
                                    )}
                                    {position && (
                                        <span className="inline-flex items-center rounded-full bg-white/70 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-900">
                                            {position}
                                        </span>
                                    )}
                                    {weightClass && (
                                        <span className="inline-flex items-center rounded-full bg-white/70 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-900">
                                            {weightClass}
                                        </span>
                                    )}
                                    {ageCategory && (
                                        <span className="inline-flex items-center rounded-full bg-white/70 border border-blue-200 px-3 py-1 text-xs font-medium text-blue-900">
                                            {ageCategory}
                                        </span>
                                    )}
                                </div>
                            )}
                            {clubLink && (
                                <a
                                    href={clubLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors font-medium"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    <span>Club / team website</span>
                                </a>
                            )}
                        </div>
                        <div className="ml-4 shrink-0">
                            <Trophy className="h-8 w-8 text-blue-600" />
                        </div>
                    </div>
                </div>
            )}

            {achievements && (
                <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
                    <div className="flex items-start gap-3">
                        <Award className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Achievements</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{achievements}</p>
                        </div>
                    </div>
                </div>
            )}

            {sponsors && (
                <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5">
                    <div className="flex items-start gap-3">
                        <Handshake className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Current Sponsors / Supporters</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{sponsors}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
